<div class="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50">
  <!-- Modern Page Header -->
  <div class="bg-white shadow-sm border-b border-slate-200">
    <div class="max-w-7xl mx-auto px-6 py-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-slate-900 mb-2">My Audits</h1>
          <p class="text-slate-600 flex items-center gap-2">
            <span nz-icon nzType="user" class="text-purple-500"></span>
            Track your current audit and view your audit history
          </p>
        </div>
        <div class="flex gap-3">
          <button
            (click)="refresh()"
            class="btn btn-outline btn-primary btn-md gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
            [class.loading]="auditsInProgressQuery.isRefetching() || auditHistoryQuery.isRefetching()"
          >
            <span nz-icon nzType="reload" [nzSpin]="auditsInProgressQuery.isRefetching() || auditHistoryQuery.isRefetching()"></span>
            Refresh
          </button>
          <button
            [routerLink]="['/audits/ready']"
            class="btn btn-primary btn-md gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <span nz-icon nzType="unordered-list"></span>
            View Available Audits
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="max-w-7xl mx-auto px-6 py-8">
    <!-- Stats Cards -->
    <div *ngIf="auditStatsQuery.data() as stats" class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      <div class="stats shadow-lg bg-white border border-slate-200 hover:shadow-xl transition-shadow">
        <div class="stat">
          <div class="stat-figure text-blue-600">
            <span nz-icon nzType="loading-3-quarters" class="text-4xl"></span>
          </div>
          <div class="stat-title text-slate-600">In Progress</div>
          <div class="stat-value text-blue-600">{{ stats.inProgress }}</div>
          <div class="stat-desc text-slate-500">Current audit</div>
        </div>
      </div>
      
      <div class="stats shadow-lg bg-white border border-slate-200 hover:shadow-xl transition-shadow">
        <div class="stat">
          <div class="stat-figure text-green-600">
            <span nz-icon nzType="calendar" class="text-4xl"></span>
          </div>
          <div class="stat-title text-slate-600">Today</div>
          <div class="stat-value text-green-600">{{ stats.completedToday }}</div>
          <div class="stat-desc text-slate-500">Audits completed</div>
        </div>
      </div>
      
      <div class="stats shadow-lg bg-white border border-slate-200 hover:shadow-xl transition-shadow">
        <div class="stat">
          <div class="stat-figure text-purple-600">
            <span nz-icon nzType="bar-chart" class="text-4xl"></span>
          </div>
          <div class="stat-title text-slate-600">This Week</div>
          <div class="stat-value text-purple-600">{{ stats.completedThisWeek }}</div>
          <div class="stat-desc text-slate-500">Audits completed</div>
        </div>
      </div>
      
      <div class="stats shadow-lg bg-white border border-slate-200 hover:shadow-xl transition-shadow">
        <div class="stat">
          <div class="stat-figure text-orange-600">
            <span nz-icon nzType="clock-circle" class="text-4xl"></span>
          </div>
          <div class="stat-title text-slate-600">Avg Duration</div>
          <div class="stat-value text-orange-600 text-2xl">{{ formatDuration(stats.averageDuration) }}</div>
          <div class="stat-desc text-slate-500">Per audit</div>
        </div>
      </div>
    </div>

    <!--
      Audits in progress — ALL of them.

      Oversight roles may hold several at once, so this lists every open audit with its own
      Resume and Cancel. One that no screen names is one that goes missing.
    -->
    <div *ngIf="hasAuditInProgress()" class="mb-8">
      <h2 class="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
        <span nz-icon nzType="fire" nzTheme="fill" class="text-orange-500"></span>
        {{ auditsInProgress().length === 1 ? 'Current Audit in Progress' : auditsInProgress().length + ' Audits in Progress' }}
      </h2>

      <div class="space-y-4">
      <div
        *ngFor="let currentAudit of auditsInProgress()"
        class="card bg-gradient-to-r from-orange-50 via-white to-orange-50 shadow-2xl border-l-4 border-orange-500"
      >
        <div class="card-body p-6">
          <div class="flex items-center gap-4">
            <!-- Audit Badge -->
            <div class="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <span class="text-white font-bold text-lg">{{ currentAudit.auditNumber }}</span>
            </div>

            <!-- Audit Info -->
            <div class="flex-1 min-w-0">
              <h3 class="text-lg font-bold text-slate-900 mb-2 truncate">{{ currentAudit.routeName }}</h3>
              <div class="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                <div class="flex items-center gap-1">
                  <span nz-icon nzType="clock-circle" class="text-orange-500"></span>
                  <span>Started {{ currentAudit.auditReadyAt | amTimeAgo }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <span nz-icon nzType="calendar" class="text-slate-400"></span>
                  <span>{{ currentAudit.auditReadyAt | date: 'short' }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <span class="badge badge-warning badge-sm gap-1">
                    <span nz-icon nzType="loading-3-quarters" nzSpin></span>
                    In Progress
                  </span>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex gap-3 flex-shrink-0">
              <button
                class="btn btn-primary btn-lg gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                (click)="resumeAudit(currentAudit.id)"
              >
                <span nz-icon nzType="play-circle" nzTheme="fill"></span>
                Resume Audit
              </button>
              <button
                class="btn btn-error btn-outline btn-md gap-2"
                (click)="cancelAudit(currentAudit)"
                [class.loading]="cancelAuditMutation.isPending()"
              >
                <span nz-icon nzType="close-circle"></span>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- No Current Audit Message -->
    <div *ngIf="auditsInProgressQuery.isSuccess() && !hasAuditInProgress()" class="mb-8">
      <div class="alert alert-info shadow-lg border border-blue-200">
        <div class="flex items-center gap-3">
          <span nz-icon nzType="info-circle" class="text-2xl text-blue-500"></span>
          <div>
            <p class="font-semibold text-blue-900">No Audit in Progress</p>
            <p class="text-sm text-blue-700">
              You don't have any audit in progress. Visit 
              <a [routerLink]="['/audits/ready']" class="link link-primary font-semibold">Available Audits</a> 
              to start one.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State for Current Audit -->
    <div *ngIf="auditsInProgressQuery.isPending()" class="mb-8">
      <div class="card bg-white shadow-xl animate-pulse">
        <div class="card-body">
          <div class="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div class="h-20 bg-slate-200 rounded"></div>
        </div>
      </div>
    </div>

    <!-- Audit History Section -->
    <div>
      <h2 class="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
        <span nz-icon nzType="history" class="text-green-500"></span>
        Completed Audits
      </h2>

      <!-- Loading State for History -->
      <div *ngIf="auditHistoryQuery.isPending()" class="space-y-4">
        <div class="card bg-white shadow-lg animate-pulse">
          <div class="card-body p-4">
            <div class="h-16 bg-slate-200 rounded"></div>
          </div>
        </div>
        <div class="card bg-white shadow-lg animate-pulse">
          <div class="card-body p-4">
            <div class="h-16 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>

      <!-- Empty History State -->
      <div *ngIf="auditHistoryQuery.isSuccess() && (!auditHistoryQuery.data() || auditHistoryQuery.data()?.length === 0)">
        <div class="card bg-white shadow-lg">
          <div class="card-body text-center py-12">
            <div class="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span nz-icon nzType="inbox" class="text-5xl text-slate-400"></span>
            </div>
            <h3 class="text-xl font-semibold text-slate-700 mb-2">No Completed Audits Yet</h3>
            <p class="text-slate-500">Your completed audits will appear here once you finish your first audit.</p>
          </div>
        </div>
      </div>

      <!-- History List -->
      <div *ngIf="auditHistoryQuery.isSuccess() && auditHistoryQuery.data() as audits">
        <div *ngIf="audits.length > 0" class="space-y-4">
          <div
            *ngFor="let audit of audits; let i = index"
            class="card bg-white shadow-md hover:shadow-lg transition-all duration-300 border-l-4 border-green-500"
          >
            <div class="card-body p-4">
              <div class="flex items-center gap-4">
                <!-- Success Badge -->
                <div class="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
                  <span nz-icon nzType="check-circle" nzTheme="fill" class="text-white text-xl"></span>
                </div>

                <!-- Audit Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <span class="badge badge-success">{{ audit.auditNumber }}</span>
                    <h3 class="text-lg font-bold text-slate-900 truncate">{{ audit.routeName }}</h3>
                  </div>
                  <div class="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                    <span class="flex items-center gap-1">
                      <span nz-icon nzType="check-circle" class="text-green-500"></span>
                      Completed {{ audit.auditCompletedAt | amTimeAgo }}
                    </span>
                    <span *ngIf="audit.auditDuration">
                      • Duration: {{ formatDuration(audit.auditDuration) }}
                    </span>
                    <span>
                      • {{ audit.auditCompletedAt | date: 'short' }}
                    </span>
                  </div>
                </div>

                <!-- View Report Button -->
                <button
                  class="btn btn-ghost btn-sm gap-2 flex-shrink-0"
                  (click)="viewReport(audit)"
                >
                  <span nz-icon nzType="file-text"></span>
                  View Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State for History -->
      <div *ngIf="auditHistoryQuery.isError()">
        <div class="card bg-white shadow-lg border-2 border-red-200">
          <div class="card-body text-center py-12">
            <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span nz-icon nzType="exclamation-circle" class="text-5xl text-red-500"></span>
            </div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">
              Failed to Load Audit History
            </h3>
            <p class="text-slate-600 mb-6">
              There was an error loading your audit history. Please try again.
            </p>
            <button
              (click)="refresh()"
              class="btn btn-error gap-2"
            >
              <span nz-icon nzType="reload"></span>
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

